// stores/user-collection.ts
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { GET_SINGLE_USER_COLLECTION, GET_SHARED_COLLECTION_SIBLING_ENTRIES } from '@/queries'
import { useUserSyncStore } from '@/stores/user-sync'
import { useInfoStore } from '@/stores/info'
import {
  type GetSingleUserCollectionQuery,
  ReleaseEntryStatusType,
  UserMiscPossessionActivityTypeType,
  UserReleaseEntryStatus,
  UserReleaseEntryStatusCollectionStatusType,
  UserReleaseEntryStatusCurrentReadStatusType,
  UserReleaseReadStatus,
} from '../../../__generated__/graphql'
import { match, P } from 'ts-pattern'
import { apolloClient } from '@/vendor/apollo-client.ts'
import { BACKGROUND_OPERATION } from '@/utils/backgroundOperation'
import { v4 as uuidv4 } from 'uuid'
import {
  CreateCollectionEntryData,
  getChangedMiscIdsFromAction,
  getMiscChangeDirection,
  ResolvedServerIds,
  shouldSuppressSiblingToast,
  UserCollectionActionSync,
  UserCollectionActionType,
  useSyncingTool,
} from '@/stores/user-collection/sync'
import { newClientId } from '@/stores/sync/clientIdentity.ts'
import { createPersistedCache } from '@/stores/sync/createPersistedCache.ts'
import {
  COLLECTION_STATUS_LABEL_MAP,
  getVolumeNumberFromVersion,
  READ_STATUS_LABEL_MAP,
} from '@/utils'
import { extractCollectionMap } from './extractCollectionMap'
import { useUserListsStore, type EntryCollectionState } from '@/stores/user-lists'
import { showToast } from '@/utils/showToast'
import { sendError } from '@/utils/sendError.ts'
import { LocalizationStore, TranslationKey, useLocalizationStore } from '@/stores/localization.ts'

export type MiscOwnedMap = {
  [miscId: string]: { type: UserMiscPossessionActivityTypeType; quantity: number }
}

export type UserCollection = {
  // Stable local identity, minted at create; the identifier every sync action
  // references. Survives the delta fold (reconciled by serverId), so it bridges
  // an offline-created entity to its server row once the create syncs.
  clientId: string
  // Server id (the UserReleaseEntryStatus row); null until this entity's create syncs.
  id: string | null
  entryId: string
  entryCompleted: boolean
  sortedVolumeVersions: string[]
  notReleasedVolumeNumbers: string[]
  ownedVolumes: number
  readVolumes: number
  releasedVolumes: number
  ownedMap: { [itemId: string]: number }
  miscOwnedMap: MiscOwnedMap
  latestReadId: string | null
  latestReadMap: { [itemId: string]: boolean }
  startYear: number | null
  startMonth: number | null
  endYear: number | null
  endMonth: number | null
  // The user's collecting window on UserReleaseEntryStatus (raw calendar-day
  // strings), distinct from the entry's publication dates above. Drives the
  // offline filteredNovelties target-month overlap; mirrored verbatim to SQLite.
  collectionStartDate: string | null
  collectionEndDate: string | null
  currentReadStatus: UserReleaseEntryStatusCurrentReadStatusType
  collectionStatus: UserReleaseEntryStatusCollectionStatusType
}

export type UserCollectionMap = {
  [entryId: string]: UserCollection
}

// The synced base carries the minted clientId alongside the server row, so the
// clientId↔serverId pairing survives the delta fold and app restart (the server
// row itself never knows our clientId). extractCollectionMap reads it.
export type ClientCollectionRow = UserReleaseEntryStatus & { clientId: string }

export function isEntryStatusComplete(status: ReleaseEntryStatusType | null | undefined): boolean {
  if (!status) {
    return false
  }
  return ['canceled', 'done'].includes(status)
}

export type UserCollectionStore = Omit<
  ReturnType<typeof useUserCollectionStore>,
  keyof ReturnType<typeof defineStore>
>

export async function onStatusChanged(
  kind: 'read' | 'collection',
  oldStatus:
    | UserReleaseEntryStatusCollectionStatusType
    | UserReleaseEntryStatusCurrentReadStatusType,
  newStatus:
    | UserReleaseEntryStatusCollectionStatusType
    | UserReleaseEntryStatusCurrentReadStatusType,
  t: LocalizationStore['t'],
) {
  if (oldStatus === newStatus) {
    return
  }
  showToast({
    text: t(kind === 'read' ? 'statusChangeRead' : 'statusChangeCollection', {
      oldStatus: t(
        COLLECTION_STATUS_LABEL_MAP[oldStatus as UserReleaseEntryStatusCollectionStatusType] ||
          READ_STATUS_LABEL_MAP[oldStatus as UserReleaseEntryStatusCurrentReadStatusType],
      ),
      newStatus: t(
        COLLECTION_STATUS_LABEL_MAP[newStatus as UserReleaseEntryStatusCollectionStatusType] ||
          READ_STATUS_LABEL_MAP[newStatus as UserReleaseEntryStatusCurrentReadStatusType],
      ),
    }),
    duration: 'long',
  })
}

export function getNewOwnedCollectionStatus(
  ownedMap: UserCollection['ownedMap'],
  newOwnedMap: UserCollection['ownedMap'],
  entry: {
    sortedVolumeVersions: string[]
    collectionStatus: UserReleaseEntryStatusCollectionStatusType
    entryCompleted: boolean
  },
): UserCollection['collectionStatus'] {
  const releaseVolumesUnknown = entry.sortedVolumeVersions.length === 0
  if (releaseVolumesUnknown) {
    return entry.collectionStatus
  }
  const ownedVolumesBefore = calcOwnedVolumes({
    ownedMap,
    sortedVolumeVersions: entry.sortedVolumeVersions,
  })
  const ownedVolumesAfter = calcOwnedVolumes({
    ownedMap: newOwnedMap,
    sortedVolumeVersions: entry.sortedVolumeVersions,
  })
  const hadOneOfEachVolume = hasOneOfEachVolumeNumber(ownedMap, entry.sortedVolumeVersions)
  const hasOneOfEachVolume = hasOneOfEachVolumeNumber(newOwnedMap, entry.sortedVolumeVersions)

  if (
    entry.collectionStatus === UserReleaseEntryStatusCollectionStatusType.Paused ||
    entry.collectionStatus === UserReleaseEntryStatusCollectionStatusType.Planned
  ) {
    if (ownedVolumesAfter > ownedVolumesBefore) {
      return entry.entryCompleted && !hadOneOfEachVolume && hasOneOfEachVolume
        ? UserReleaseEntryStatusCollectionStatusType.Done
        : UserReleaseEntryStatusCollectionStatusType.Collecting
    }
  }
  if (entry.collectionStatus === UserReleaseEntryStatusCollectionStatusType.Collecting) {
    if (ownedVolumesBefore !== 0 && ownedVolumesAfter === 0) {
      return UserReleaseEntryStatusCollectionStatusType.Planned
    }
  }
  return entry.entryCompleted && !hadOneOfEachVolume && hasOneOfEachVolume
    ? UserReleaseEntryStatusCollectionStatusType.Done
    : entry.collectionStatus
}

export function getNewReadCollectionStatus(
  latestReadMap: UserCollection['latestReadMap'],
  newLatestReadMap: UserCollection['latestReadMap'],
  entry: {
    sortedVolumeVersions: string[]
    currentReadStatus: UserReleaseEntryStatusCurrentReadStatusType
    entryCompleted: boolean
  },
): UserCollection['currentReadStatus'] {
  const releaseVolumesUnknown = entry.sortedVolumeVersions.length === 0
  if (releaseVolumesUnknown) {
    return entry.currentReadStatus
  }
  const previousVolumesRead = calcReadVolumes({
    latestReadMap,
    sortedVolumeVersions: entry.sortedVolumeVersions,
  })
  const afterVolumesRead = calcReadVolumes({
    latestReadMap: newLatestReadMap,
    sortedVolumeVersions: entry.sortedVolumeVersions,
  })
  const hadReadAllVolumes = readAllVolumes(latestReadMap, entry.sortedVolumeVersions)
  const hasReadAllVolumes = readAllVolumes(newLatestReadMap, entry.sortedVolumeVersions)

  if (
    entry.currentReadStatus === UserReleaseEntryStatusCurrentReadStatusType.Paused ||
    entry.currentReadStatus === UserReleaseEntryStatusCurrentReadStatusType.Planned
  ) {
    if (afterVolumesRead > previousVolumesRead) {
      return entry.entryCompleted && !hadReadAllVolumes && hasReadAllVolumes
        ? UserReleaseEntryStatusCurrentReadStatusType.Done
        : UserReleaseEntryStatusCurrentReadStatusType.Reading
    }
  }
  if (entry.currentReadStatus === UserReleaseEntryStatusCurrentReadStatusType.Reading) {
    if (previousVolumesRead !== 0 && afterVolumesRead === 0) {
      return UserReleaseEntryStatusCurrentReadStatusType.Planned
    }
  }
  return entry.entryCompleted && !hadReadAllVolumes && hasReadAllVolumes
    ? UserReleaseEntryStatusCurrentReadStatusType.Done
    : entry.currentReadStatus
}

const collectionCache = createPersistedCache<ClientCollectionRow[]>('userCollectionData', [])
// The pending sync queue is persisted so collection edits made offline survive an
// app restart and reach the server once back online (see drainPendingSync).
const actionStackCache = createPersistedCache<UserCollectionActionSync[]>(
  'userCollectionActionStack',
  [],
)

export const useUserCollectionStore = defineStore('user-collection', () => {
  const actionStack = ref<UserCollectionActionSync[]>([])
  const userCollectionData = ref<ClientCollectionRow[] | null>(null)
  const { t } = useLocalizationStore()

  const { connected } = storeToRefs(useInfoStore())
  const { syncStatus, startSync } = useSyncingTool(apolloClient)

  // Fold the delta-synced base (user-sync store) into the client collection base
  // WITHOUT dropping clientId. The server row is keyed only by its server id and
  // never knows our clientId, so we reconcile: an incoming row inherits the
  // clientId of the entity that already carries its server id — either a previous
  // client row, or a just-synced createCollectionEntry action (its collectionId
  // now points at this row). Rows with no local origin (created on another device)
  // mint a fresh clientId. Read the action stack BEFORE dropping synced actions.
  const applyFromSync = () => {
    const userSync = useUserSyncStore()
    const clientIdByServerId = new Map<string, string>()
    for (const row of userCollectionData.value ?? []) {
      if (row.id) clientIdByServerId.set(row.id, row.clientId)
    }
    for (const action of actionStack.value) {
      if (action.type.type === 'createCollectionEntry' && action.type.data.collectionId) {
        clientIdByServerId.set(action.type.data.collectionId, action.type.data.clientId)
      }
    }
    const reconciled: ClientCollectionRow[] = userSync.collectionRows.map((row) => ({
      ...row,
      clientId: (row.id ? clientIdByServerId.get(row.id) : undefined) ?? newClientId(),
    }))
    userCollectionData.value = reconciled
    void collectionCache.save(reconciled)
    actionStack.value = actionStack.value.filter((action) => !action.sync.synced)
  }

  // Same guard as the lists base: a sync that never touched the replica (skipped
  // while disconnected, or failed) must not fold an empty feed into the base and
  // persist it over the cached collection.
  const loadUserCollections = async () => {
    const userSync = useUserSyncStore()
    const synced = await userSync.sync()
    if (!synced && !userSync.hasReplica) return
    applyFromSync()
  }

  const fetchSingleCollectionEntry = async (entryId: string): Promise<string | null> => {
    try {
      const result = await apolloClient.query<GetSingleUserCollectionQuery>({
        query: GET_SINGLE_USER_COLLECTION,
        variables: { where: { entry: { id: { equals: entryId } } } },
        fetchPolicy: 'network-only',
        context: BACKGROUND_OPERATION,
      })
      const entry = result.data?.userReleaseEntryStatuses?.[0]
      if (entry && userCollectionData.value) {
        const existing = userCollectionData.value.find((c) => c.id === entry.id)
        const clientRow: ClientCollectionRow = {
          ...entry,
          clientId: existing?.clientId ?? newClientId(),
        }
        userCollectionData.value = existing
          ? userCollectionData.value.map((c) => (c.id === entry.id ? clientRow : c))
          : [...userCollectionData.value, clientRow]
        return entry.entry?.internalName ?? null
      }
      return null
    } catch (e) {
      console.error('Failed to fetch single collection entry', e)
      return null
    }
  }

  // A synced misc (bundle/slipcase) change may belong to a shared collection
  // group; the server then created/updated this user's statuses on the OTHER
  // releases of that group. Pull those sibling statuses so the app reflects
  // them without a full reload.
  const refreshSharedSiblingEntries = async (action: UserCollectionActionType) => {
    const miscIds = getChangedMiscIdsFromAction(action)
    if (miscIds.length === 0) return
    try {
      const result = await apolloClient.query({
        query: GET_SHARED_COLLECTION_SIBLING_ENTRIES,
        variables: { where: { id: { in: miscIds } } },
        fetchPolicy: 'network-only',
        context: BACKGROUND_OPERATION,
      })
      const sourceEntryId = action.data.entryId
      const siblingEntryIds = new Set<string>()
      for (const collection of result.data?.releaseItemCollections ?? []) {
        for (const member of collection.sharedGroup?.collections ?? []) {
          if (member.entryId && member.entryId !== sourceEntryId) {
            siblingEntryIds.add(member.entryId)
          }
        }
      }
      const refreshedTitles = (
        await Promise.all(
          [...siblingEntryIds].map((entryId) => fetchSingleCollectionEntry(entryId)),
        )
      ).filter((title): title is string => title !== null && title.length > 0)
      if (refreshedTitles.length > 0 && !shouldSuppressSiblingToast(action)) {
        const toastKey = match(getMiscChangeDirection(action))
          .returnType<TranslationKey>()
          .with('added', () => 'sharedCollectionSiblingsAdded')
          .with('removed', () => 'sharedCollectionSiblingsRemoved')
          .otherwise(() => 'sharedCollectionSiblingsUpdated')
        showToast({
          text: t(toastKey, { titles: refreshedTitles.join(', ') }),
          duration: 'long',
        })
      }
    } catch (e) {
      console.error('Failed to refresh shared sibling entries', e)
    }
  }

  const init = async () => {
    const base = migrateBaseRows(await collectionCache.load())
    userCollectionData.value = base
    actionStack.value = migratePersistedActions(await actionStackCache.load(), base)
    const userSync = useUserSyncStore()
    const synced = await userSync.init()
    if (synced || userSync.hasReplica) applyFromSync()
  }

  const collectionLoaded = computed(() => userCollectionData.value !== null)

  const collectionMap = computed(() => {
    if (!userCollectionData.value) {
      return {}
    }
    const map = extractCollectionMap(userCollectionData.value)
    applyActionStack(actionStack.value, map)
    return map
  })

  // Server sync runs online-only. Offline, edits stay queued on the (persisted)
  // actionStack; collectionMap still reflects them locally, and the SQLite mirror
  // reads that. drainPendingSync resumes them on reconnect.
  const resolveServerIds = (clientId: string): ResolvedServerIds =>
    resolveServerIdsFrom(clientId, userCollectionData.value ?? [], actionStack.value)

  const drainPendingSync = () => {
    if (!connected.value) return
    if (syncStatus.value?.type === 'inProgress') return
    const latestNotSyncedAction = actionStack.value.find((action) => !action.sync.synced)
    if (latestNotSyncedAction) {
      startSync(latestNotSyncedAction, resolveServerIds(latestNotSyncedAction.type.data.clientId))
    }
  }

  watch(actionStack, () => void actionStackCache.save(actionStack.value), { deep: true })
  // Resume the queue whenever connectivity returns — reconnect, forced-offline
  // toggled off, or the server recovering. Edits made while disconnected were held
  // (not dropped), so they drain here.
  watch(connected, (isConnected) => {
    if (isConnected) drainPendingSync()
  })
  // Reflect background delta syncs (e.g. on reconnect) into the collection base.
  watch(
    () => useUserSyncStore().revision,
    () => applyFromSync(),
  )

  watch(
    actionStack,
    () => {
      drainPendingSync()
    },
    { deep: true },
  )

  watch([syncStatus], () => {
    match(syncStatus?.value)
      .with({ type: 'inProgress' }, () => {})
      .with({ type: 'success' }, ({ syncId }) => {
        const syncedAction = actionStack.value.find((action) => action.sync.id === syncId)
        if (syncedAction) {
          syncedAction.sync.synced = true
          refreshSharedSiblingEntries(syncedAction.type)
          pruneListsForEntry(syncedAction.type.data.entryId)
        }
      })
      .with(
        { type: 'creationSuccess' },
        ({ syncId, creationResult: { collectionId, latestReadId } }) => {
          const syncedAction = actionStack.value.find((action) => action.sync.id === syncId)
          if (syncedAction && syncedAction.type.type === 'createCollectionEntry') {
            // Attach the server ids to the create action so the resolver can hand
            // them to this entity's still-pending edits until the delta fold folds
            // the row (with its clientId) into the client base.
            syncedAction.type.data.collectionId = collectionId
            syncedAction.type.data.readId = latestReadId
            syncedAction.sync.synced = true
            refreshSharedSiblingEntries(syncedAction.type)
            pruneListsForEntry(syncedAction.type.data.entryId)
          }
        },
      )
      .with({ type: 'error' }, ({ syncId, error }) => {
        const syncedAction = actionStack.value.find((action) => action.sync.id === syncId)

        // Server down: the edit couldn't be pushed. Keep it on the (persisted)
        // actionStack so it drains once the server is back — never drop user data,
        // regardless of entitlement. The connected watch drains it on recovery.
        if (error.type === 'serverDown') {
          syncStatus.value = null
          return
        }

        // Real rejection: the server answered with an error, so retrying can't help.
        // Roll the optimistic edit back and tell the user.
        sendError(error.error, {
          source: 'collection.sync',
          actionType: syncedAction?.type.type ?? 'unknown',
        })
        showToast({ text: t('collectionSyncError'), duration: 'long' })
        if (!syncedAction) return

        if (syncedAction.type.type === 'createCollectionEntry') {
          const entryId = syncedAction.type.data.entryId
          actionStack.value = actionStack.value.filter(
            (action) => entryId !== action.type.data.entryId,
          )
          fetchSingleCollectionEntry(entryId)
        } else {
          actionStack.value = actionStack.value.filter((action) => action.sync.id !== syncId)
        }
      })
      .with(P.nullish, () => {})
      .exhaustive()
  })

  // After a collection change for an entry persists, let the lists store drop
  // that item from any curated list set to remove-when-collected/read. Runs
  // post-sync so a rolled-back collection change never triggers a removal.
  const entryIdsAwaitingListsLoad = new Set<string>()

  const pruneListsForEntry = (entryId: string) => {
    const lists = useUserListsStore()
    if (!lists.customListsLoaded) {
      entryIdsAwaitingListsLoad.add(entryId)
      return
    }
    const coll = collectionMap.value[entryId]
    if (!coll) return
    const state: EntryCollectionState = {
      collectionStatus: coll.collectionStatus,
      currentReadStatus: coll.currentReadStatus,
      ownedByVersion: coll.ownedMap,
      readByVolume: coll.latestReadMap,
      miscOwnedMap: coll.miscOwnedMap,
    }
    lists.pruneRemovedForEntry(entryId, state)
  }

  watch(
    () => useUserListsStore().customListsLoaded,
    (loaded) => {
      if (!loaded) return
      const waiting = [...entryIdsAwaitingListsLoad]
      entryIdsAwaitingListsLoad.clear()
      waiting.forEach(pruneListsForEntry)
    },
  )

  const pushAction = (action: UserCollectionActionType) => {
    const newAction = {
      type: action,
      sync: { id: uuidv4(), synced: false },
    }
    actionStack.value.push(newAction)
  }

  // Drop every still-pending action for a clientId whose create has not synced —
  // an entity created and then removed offline never needs to reach the server.
  const annihilateUnsyncedCreate = (clientId: string): boolean => {
    const hasUnsyncedCreate = actionStack.value.some(
      (action) =>
        action.type.type === 'createCollectionEntry' &&
        action.type.data.clientId === clientId &&
        !action.sync.synced,
    )
    if (!hasUnsyncedCreate) return false
    actionStack.value = actionStack.value.filter((action) => action.type.data.clientId !== clientId)
    return true
  }

  const createCollectionEntry = (data: CreateCollectionEntryData) => {
    if (collectionMap.value?.[data.entryId]) return
    pushAction({
      type: 'createCollectionEntry',
      data: { ...data, clientId: newClientId() },
    })
  }

  const removeCollectionEntry = (entryId: string) => {
    const entry = collectionMap.value?.[entryId]
    if (!entry) return
    if (annihilateUnsyncedCreate(entry.clientId)) return
    pushAction({
      type: 'removeCollectionEntry',
      data: {
        clientId: entry.clientId,
        entryId,
        deletionDate: new Date().toISOString(),
      },
    })
  }

  const updateOwned = (
    entryId: UserCollection['entryId'],
    volumeVersion: string,
    ownedAmount: number,
  ) => {
    if (!collectionMap.value?.[entryId]) return
    const { clientId, ownedMap } = collectionMap.value[entryId]
    const newOwnedMap = {
      ...ownedMap,
      [volumeVersion]: ownedAmount,
    }

    const oldCollectionStatus = collectionMap.value[entryId].collectionStatus
    const newCollectionStatus = getNewOwnedCollectionStatus(
      ownedMap,
      newOwnedMap,
      collectionMap.value[entryId],
    )
    onStatusChanged('collection', oldCollectionStatus, newCollectionStatus, t)

    pushAction({
      type: 'updateCollectionData',
      data: {
        clientId,
        entryId,
        collectionData: {
          ownedMap: newOwnedMap,
          collectionStatus: newCollectionStatus,
        },
      },
    })
  }

  const updateOwnedMap = (
    entryId: UserCollection['entryId'],
    updatedOwnedMap: UserCollection['ownedMap'],
  ) => {
    if (!collectionMap.value?.[entryId]) return
    const { clientId, ownedMap } = collectionMap.value[entryId]
    const newOwnedMap = {
      ...ownedMap,
      ...updatedOwnedMap,
    }

    const oldCollectionStatus = collectionMap.value[entryId].collectionStatus
    const newCollectionStatus = getNewOwnedCollectionStatus(
      ownedMap,
      newOwnedMap,
      collectionMap.value[entryId],
    )
    onStatusChanged('collection', oldCollectionStatus, newCollectionStatus, t)

    pushAction({
      type: 'updateCollectionData',
      data: {
        clientId,
        entryId,
        collectionData: {
          ownedMap: newOwnedMap,
          collectionStatus: newCollectionStatus,
        },
      },
    })
  }

  const updateMiscOwned = (
    entryId: UserCollection['entryId'],
    miscId: string,
    type: UserMiscPossessionActivityTypeType,
    quantity: number,
    options?: { suppressSiblingToast?: boolean },
  ) => {
    if (!collectionMap.value?.[entryId]) return
    const { clientId, miscOwnedMap } = collectionMap.value[entryId]
    const newMiscOwnedMap = {
      ...miscOwnedMap,
      [miscId]: { type, quantity },
    }

    pushAction({
      type: 'updateCollectionData',
      data: {
        clientId,
        entryId,
        collectionData: {
          miscOwnedMap: newMiscOwnedMap,
        },
        suppressSiblingToast: options?.suppressSiblingToast,
      },
    })
  }

  const updateLatestRead = (
    entryId: UserCollection['entryId'],
    volumeVersion: string,
    read: boolean,
  ) => {
    if (!collectionMap.value?.[entryId]) return
    const { clientId, latestReadMap } = collectionMap.value[entryId]
    const volumeNumber = getVolumeNumberFromVersion(volumeVersion)
    const newLatestReadMap = {
      ...latestReadMap,
      [volumeNumber]: read,
    }
    const newCollectionReadStatus = getNewReadCollectionStatus(
      latestReadMap,
      newLatestReadMap,
      collectionMap.value[entryId],
    )
    if (newCollectionReadStatus !== collectionMap.value[entryId].currentReadStatus) {
      onStatusChanged(
        'read',
        collectionMap.value[entryId].currentReadStatus,
        newCollectionReadStatus,
        t,
      )
      pushAction({
        type: 'updateCollectionData',
        data: {
          clientId,
          entryId,
          collectionData: {
            currentReadStatus: newCollectionReadStatus,
          },
        },
      })
    }
    pushAction({
      type: 'updateEntryReadMap',
      data: {
        clientId,
        entryId,
        latestReadMap: newLatestReadMap,
      },
    })
  }

  const updateCollectionData = (
    entryId: UserCollection['entryId'],
    collectionData: Partial<UserCollection>,
  ) => {
    if (!collectionMap.value?.[entryId]) {
      sendError(new Error(`updateCollectionData: entry ${entryId} not in collectionMap`), {
        source: 'collection.update',
      })
      return
    }
    const { clientId } = collectionMap.value[entryId]

    if (collectionData.ownedMap) {
      const oldCollectionStatus = collectionMap.value[entryId].collectionStatus
      const newCollectionStatus = getNewOwnedCollectionStatus(
        collectionMap.value[entryId].ownedMap,
        collectionData.ownedMap,
        collectionMap.value[entryId],
      )
      onStatusChanged('collection', oldCollectionStatus, newCollectionStatus, t)
      collectionData.collectionStatus = newCollectionStatus
    }

    pushAction({
      type: 'updateCollectionData',
      data: {
        clientId,
        entryId,
        collectionData,
      },
    })
  }

  const updateLatestReadMap = (
    entryId: UserCollection['entryId'],
    newLatestReadMap: UserCollection['latestReadMap'],
  ) => {
    if (!collectionMap.value?.[entryId]) {
      sendError(new Error(`updateLatestReadMap: entry ${entryId} not in collectionMap`), {
        source: 'collection.updateReadMap',
      })
      return
    }
    const { clientId } = collectionMap.value[entryId]

    const newCollectionReadStatus = getNewReadCollectionStatus(
      collectionMap.value[entryId].latestReadMap,
      newLatestReadMap,
      collectionMap.value[entryId],
    )
    if (newCollectionReadStatus !== collectionMap.value[entryId].currentReadStatus) {
      onStatusChanged(
        'read',
        collectionMap.value[entryId].currentReadStatus,
        newCollectionReadStatus,
        t,
      )
      pushAction({
        type: 'updateCollectionData',
        data: {
          clientId,
          entryId,
          collectionData: {
            currentReadStatus: newCollectionReadStatus,
          },
        },
      })
    }

    pushAction({
      type: 'updateEntryReadMap',
      data: {
        clientId,
        entryId,
        latestReadMap: newLatestReadMap,
      },
    })
  }

  function getLatestNotOwnedVolumeVersions(
    userCollectionMap: UserCollectionMap,
    entryId: string,
  ): string[] | null {
    const ownedMap = userCollectionMap?.[entryId]?.ownedMap
    const sortedVolumeVersions = userCollectionMap?.[entryId]?.sortedVolumeVersions
    if (!ownedMap || !sortedVolumeVersions) return null
    const sortedVolumeNumbers = getVolumeNumbersFromVersions(sortedVolumeVersions)
    const ownedMapByVolumeNumber = getOwnedMapByVolumeNumber(ownedMap)
    const notYetOwnedVolumeVersion = sortedVolumeNumbers.find(
      (volumeNo) => !ownedMapByVolumeNumber[volumeNo],
    )
    if (!notYetOwnedVolumeVersion) return null
    const n = getVolumeNumberFromVersion(notYetOwnedVolumeVersion)
    return sortedVolumeVersions.filter((volumeNo) => volumeNo.startsWith(n))
  }

  function getLatestNotReadVolumeNo(
    userCollectionMap: UserCollectionMap,
    entryId: string,
  ): string | null {
    const latestReadMap = userCollectionMap?.[entryId]?.latestReadMap
    const sortedVolumeVersions = userCollectionMap?.[entryId]?.sortedVolumeVersions
    if (!latestReadMap || !sortedVolumeVersions) return null
    const sortedVolumeNumbers = getVolumeNumbersFromVersions(sortedVolumeVersions)
    return sortedVolumeNumbers.find((v) => !latestReadMap[v]) || null
  }

  function waitForPendingSync(onProgress?: (done: number, total: number) => void): Promise<void> {
    return new Promise((resolve) => {
      const getProgress = () => {
        const total = actionStack.value.length
        const done = actionStack.value.filter((a) => a.sync.synced).length
        return { done, total }
      }
      const { done, total } = getProgress()
      onProgress?.(done, total)
      if (done === total) {
        resolve()
        return
      }
      const stop = watch(
        actionStack,
        () => {
          const { done, total } = getProgress()
          onProgress?.(done, total)
          if (done === total) {
            stop()
            resolve()
          }
        },
        { deep: true },
      )
    })
  }

  return {
    collectionMap,
    collectionLoaded,
    createCollectionEntry,
    removeCollectionEntry,
    updateOwned,
    updateOwnedMap,
    updateMiscOwned,
    updateLatestRead,
    updateCollectionData,
    updateLatestReadMap,
    getLatestNotOwnedVolumeVersions,
    getLatestNotReadVolumeNo,
    waitForPendingSync,
    init,
    loadUserCollections,
  }
})

export function hasOneOfEachVolumeNumber(
  ownedMap: UserCollection['ownedMap'],
  sortedVolumeVersions: string[],
) {
  const sortedVolumeNumbers = getVolumeNumbersFromVersions(sortedVolumeVersions)
  const ownedMapByVolumeNumber = getOwnedMapByVolumeNumber(ownedMap)
  return sortedVolumeNumbers.every((volumeNo) => ownedMapByVolumeNumber[volumeNo] > 0)
}

export function readAllVolumes(
  latestReadMap: UserCollection['latestReadMap'],
  sortedVolumeVersions: string[],
) {
  const sortedVolumeNumbers = getVolumeNumbersFromVersions(sortedVolumeVersions)
  return sortedVolumeNumbers.every((volumeNo) => latestReadMap[volumeNo])
}

export function getLatestReadStatus(
  collectionEntry: UserReleaseEntryStatus,
): UserReleaseReadStatus | undefined {
  if (collectionEntry.readStatus) {
    const readStatuses = collectionEntry.readStatus.filter((status) => status.status !== undefined)
    readStatuses.sort((a, b) => {
      if (a.startedRead && b.startedRead) {
        return new Date(b.startedRead).getTime() - new Date(a.startedRead).getTime()
      }
      return 0
    })
    return readStatuses[0]
  }
}

export function volumeNumberWithSuffix(v: {
  number: string
  volumeSuffix: string | null | undefined
}): string {
  return `${v.number}${v.volumeSuffix ? `-${v.volumeSuffix.trim()}` : ''}`
}

function detectDuplicatesInSorted(number: string, i: number, arr: string[]) {
  return i === 0 || number != arr[i - 1]
}

export function getVolumeNumbersFromVersions(sortedVolumeNumbers: string[]) {
  return sortedVolumeNumbers.map(getVolumeNumberFromVersion).filter(detectDuplicatesInSorted)
}

export function getOwnedMapByVolumeNumber(ownedMap: UserCollection['ownedMap']) {
  const ownedMapByVolumeNumber: UserCollection['ownedMap'] = {}
  Object.entries(ownedMap).forEach(([key, value]) => {
    const n = getVolumeNumberFromVersion(key)
    if (!ownedMapByVolumeNumber[n]) {
      ownedMapByVolumeNumber[n] = 0
    }
    ownedMapByVolumeNumber[n] += value
  })
  return ownedMapByVolumeNumber
}

export function calcOwnedVolumes({
  sortedVolumeVersions,
  ownedMap,
}: Pick<UserCollection, 'sortedVolumeVersions' | 'ownedMap'>) {
  const sortedVolumeNumbersWithoutSuffix = getVolumeNumbersFromVersions(sortedVolumeVersions)
  const ownedMapByVolumeNumberWithoutSuffix = getOwnedMapByVolumeNumber(ownedMap)
  return sortedVolumeNumbersWithoutSuffix.reduce(
    (acc, key) => acc + (ownedMapByVolumeNumberWithoutSuffix[key] ? 1 : 0),
    0,
  )
}

export function calcReleasedVolumes({
  sortedVolumeVersions,
  notReleasedVolumeNumbers,
}: {
  sortedVolumeVersions: string[]
  notReleasedVolumeNumbers: string[]
}) {
  const notReleased = new Set(notReleasedVolumeNumbers)
  const releasedVolumeNumbers = new Set(
    sortedVolumeVersions
      .filter((version) => !notReleased.has(version))
      .map(getVolumeNumberFromVersion),
  )
  return releasedVolumeNumbers.size
}

export function calcOwnedReleasedVolumes({
  sortedVolumeVersions,
  ownedMap,
  notReleasedVolumeNumbers,
}: Pick<UserCollection, 'sortedVolumeVersions' | 'ownedMap' | 'notReleasedVolumeNumbers'>) {
  const notReleased = new Set(notReleasedVolumeNumbers)
  const releasedVolumeNumbers = new Set(
    sortedVolumeVersions
      .filter((version) => !notReleased.has(version))
      .map(getVolumeNumberFromVersion),
  )
  const ownedMapByVolumeNumber = getOwnedMapByVolumeNumber(ownedMap)
  let count = 0
  releasedVolumeNumbers.forEach((volumeNumber) => {
    if (ownedMapByVolumeNumber[volumeNumber]) {
      count++
    }
  })
  return count
}

export function calcReadVolumes({
  sortedVolumeVersions,
  latestReadMap,
}: Pick<UserCollection, 'sortedVolumeVersions' | 'latestReadMap'>) {
  const sortedVolumeNumbersWithoutSuffix = getVolumeNumbersFromVersions(sortedVolumeVersions)
  const readByVolumeNumber: { [volumeNumber: string]: boolean } = {}
  Object.entries(latestReadMap).forEach(([version, read]) => {
    if (read) {
      readByVolumeNumber[getVolumeNumberFromVersion(version)] = true
    }
  })
  return sortedVolumeNumbersWithoutSuffix.reduce(
    (acc, key) => acc + (readByVolumeNumber[key] ? 1 : 0),
    0,
  )
}

// Resolve the target entity's server ids live, at sync time, from whichever source
// currently holds them: the client base (once the create has folded in) or the
// entity's own create action (after creationSuccess, before the fold). Serial drain
// guarantees a create resolves before any edit that depends on it.
export function resolveServerIdsFrom(
  clientId: string,
  base: ClientCollectionRow[],
  stack: UserCollectionActionSync[],
): ResolvedServerIds {
  const baseRow = base.find((row) => row.clientId === clientId)
  if (baseRow) {
    return { collectionId: baseRow.id ?? null, readId: getLatestReadStatus(baseRow)?.id ?? null }
  }
  const createAction = stack.find(
    (action) =>
      action.type.type === 'createCollectionEntry' && action.type.data.clientId === clientId,
  )
  if (createAction && createAction.type.type === 'createCollectionEntry') {
    return {
      collectionId: createAction.type.data.collectionId,
      readId: createAction.type.data.readId,
    }
  }
  return { collectionId: null, readId: null }
}

// Persisted base rows / actions from before clientId existed carry no clientId.
// A synced base row's own server id is a stable, unique client identity, so adopt
// it; a not-yet-synced create (no base row) mints one and its sibling edits inherit
// it by entryId. One-time, deterministic, runs on init.
type LegacyBaseRow = UserReleaseEntryStatus & { clientId?: string }
type LegacyActionData = { entryId: string; clientId?: string }

export function migrateBaseRows(rows: ClientCollectionRow[]): ClientCollectionRow[] {
  return (rows as LegacyBaseRow[]).map((row) => ({
    ...row,
    clientId: row.clientId ?? row.id ?? newClientId(),
  }))
}

export function migratePersistedActions(
  actions: UserCollectionActionSync[],
  base: ClientCollectionRow[],
): UserCollectionActionSync[] {
  const clientIdByEntryId = new Map<string, string>()
  for (const row of base) {
    if (row.entry?.id) clientIdByEntryId.set(row.entry.id, row.clientId)
  }
  for (const action of actions) {
    if (action.type.type !== 'createCollectionEntry') continue
    const data = action.type.data as LegacyActionData
    if (!data.clientId) data.clientId = clientIdByEntryId.get(data.entryId) ?? newClientId()
    clientIdByEntryId.set(data.entryId, data.clientId)
  }
  for (const action of actions) {
    const data = action.type.data as LegacyActionData
    if (!data.clientId) data.clientId = clientIdByEntryId.get(data.entryId) ?? newClientId()
  }
  return actions
}

function applyActionStack(stack: UserCollectionActionSync[], map: UserCollectionMap) {
  for (const action of stack) {
    match(action.type)
      .with({ type: 'createCollectionEntry' }, ({ data }) => {
        if (!map[data.entryId]) {
          map[data.entryId] = {
            clientId: data.clientId,
            id: data.collectionId,
            collectionStatus: data.collectionStatus,
            entryCompleted: data.entryCompleted,
            entryId: data.entryId,
            startYear: data.startYear,
            startMonth: data.startMonth,
            endYear: data.endYear,
            endMonth: data.endMonth,
            currentReadStatus: data.currentReadStatus,
            sortedVolumeVersions: data.sortedVolumeNumbers,
            notReleasedVolumeNumbers: data.notReleasedVolumeNumbers,
            collectionStartDate: null,
            collectionEndDate: null,
            ownedVolumes: calcOwnedVolumes({
              ownedMap: data.ownedMap,
              sortedVolumeVersions: data.sortedVolumeNumbers,
            }),
            miscOwnedMap: data.miscOwnedMap,
            ownedMap: data.ownedMap,
            releasedVolumes: calcReleasedVolumes({
              sortedVolumeVersions: data.sortedVolumeNumbers,
              notReleasedVolumeNumbers: data.notReleasedVolumeNumbers,
            }),
            readVolumes: calcReadVolumes({
              latestReadMap: data.latestReadMap,
              sortedVolumeVersions: data.sortedVolumeNumbers,
            }),
            latestReadId: data.readId,
            latestReadMap: data.latestReadMap,
          }
        }
      })
      .with({ type: 'removeCollectionEntry' }, ({ data: { entryId } }) => {
        delete map[entryId]
      })
      .with({ type: 'updateCollectionData' }, ({ data }) => {
        const entry = map[data.entryId]
        if (entry) {
          if (data.collectionData.ownedMap) {
            entry.ownedMap = data.collectionData.ownedMap
          }
          if (data.collectionData.miscOwnedMap) {
            entry.miscOwnedMap = data.collectionData.miscOwnedMap
          }
          if (data.collectionData.currentReadStatus) {
            entry.currentReadStatus = data.collectionData.currentReadStatus
          }
          if (data.collectionData.collectionStatus) {
            entry.collectionStatus = data.collectionData.collectionStatus
          }
          entry.ownedVolumes = calcOwnedVolumes(entry)
        }
      })
      .with({ type: 'updateEntryReadMap' }, ({ data }) => {
        if (map[data.entryId]) {
          map[data.entryId].latestReadMap = data.latestReadMap
          map[data.entryId].readVolumes = calcReadVolumes(map[data.entryId])
        }
      })
      .exhaustive()
  }
}
